/**
 * Platform Admin Organization Detail Page
 *
 * Shows organization details including admins/owners and allows
 * platform admins to change the subscription tier.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { usePlatformOrganization, useUpdatePlatformOrganization } from '../../hooks/queries';
import { SubscriptionTier } from '../../api/auth';
import { OrgAdmin, platformResetUserPassword, getPlatformOrganizationLogs, downloadOrgLogs, type OrgLogsResponse, type OrgBillingDetail } from '../../api/platform';
import { getErrorMessage } from '../../api/client';
import { Modal } from '../../components/ui';

const SUBSCRIPTION_TIERS: { value: SubscriptionTier; label: string }[] = [
  { value: 'FREE', label: 'Free' },
  { value: 'PRO', label: 'Pro' },
  { value: 'ENTERPRISE', label: 'Enterprise' },
];

export default function OrganizationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { organization, loading, error } = usePlatformOrganization(id || '');
  const { updateOrganization, updating, error: updateError } = useUpdatePlatformOrganization({
    onSuccess: () => {
      toast.success('Subscription tier updated');
    },
  });

  const [selectedTier, setSelectedTier] = useState<SubscriptionTier | ''>('');

  // Initialize selected tier when organization loads
  useEffect(() => {
    if (organization) {
      setSelectedTier(organization.subscriptionTier);
    }
  }, [organization]);

  const hasChanges = organization && selectedTier !== organization.subscriptionTier;

  const handleSaveTier = () => {
    if (id && selectedTier) {
      updateOrganization({ id, subscriptionTier: selectedTier });
    }
  };

  if (loading) {
    return <PageSkeleton />;
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="p-8">
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700">
          Organization not found
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Back button and header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/platform')}
          className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1 mb-4"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Dashboard
        </button>
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold text-gray-900">{organization.name}</h1>
          <TierBadge tier={organization.subscriptionTier} />
          <StatusBadge isActive={organization.isActive} />
        </div>
        <p className="text-gray-500 mt-1">/{organization.slug}</p>
      </div>

      {/* Update error */}
      {updateError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {updateError}
        </div>
      )}

      {/* Usage stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <InfoCard
          label="Members"
          value={String(organization.memberCount)}
          sub={`${organization.stats.members.owners} owner · ${organization.stats.members.admins} admin · ${organization.stats.members.crew} crew`}
        />
        <InfoCard
          label="Events this month"
          value={
            organization.stats.eventsMonthLimit === null
              ? String(organization.stats.eventsThisMonth)
              : `${organization.stats.eventsThisMonth} / ${organization.stats.eventsMonthLimit}`
          }
          sub={organization.stats.eventsMonthLimit === null ? 'unlimited plan' : 'of plan limit'}
        />
        <InfoCard label="Events all-time" value={String(organization.stats.eventsTotal)} />
        <InfoCard label="Messages (30d)" value={String(organization.stats.messagesLast30d)} />
        <InfoCard
          label="QR signups"
          value={String(organization.stats.qrJoinsTotal)}
          sub={`${organization.stats.qrLinksActive} active code${organization.stats.qrLinksActive === 1 ? '' : 's'}`}
        />
        <InfoCard label="Last activity" value={formatDate(organization.lastActivityAt)} sub={`created ${formatDate(organization.createdAt)}`} />
      </div>

      {/* Billing */}
      <BillingCard billing={organization.billing} />

      {/* Subscription Tier Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Subscription Tier</h2>
        <div className="flex items-end gap-4">
          <div className="flex-1 max-w-xs">
            <label htmlFor="tier" className="block text-sm font-medium text-gray-700 mb-1">
              Tier
            </label>
            <select
              id="tier"
              value={selectedTier}
              onChange={(e) => setSelectedTier(e.target.value as SubscriptionTier)}
              className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              disabled={updating}
            >
              {SUBSCRIPTION_TIERS.map((tier) => (
                <option key={tier.value} value={tier.value}>
                  {tier.label}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={handleSaveTier}
            disabled={!hasChanges || updating}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              hasChanges && !updating
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {updating ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Activity Log Section */}
      <ActivityLogSection orgId={id!} />

      {/* Admins Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Organization Admins</h2>
        </div>
        <AdminsTable admins={organization.admins} />
      </div>
    </div>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

// Raw Stripe/maintenance ledger types → operator-readable lines.
const BILLING_EVENT_LABELS: Record<string, string> = {
  'checkout.session.completed': 'Checkout completed',
  'customer.subscription.created': 'Subscription started',
  'customer.subscription.updated': 'Subscription updated (plan/renewal/cancel state)',
  'customer.subscription.deleted': 'Subscription ended',
  'invoice.paid': 'Invoice paid',
  'invoice.payment_failed': 'Payment FAILED',
  'invoice.upcoming': 'Renewal coming up',
};

function BillingCard({ billing }: { billing: OrgBillingDetail }) {
  const fmt = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;

  // One honest headline for the subscription state.
  let headline: { text: string; className: string };
  if (billing.cancelAtPeriodEnd && billing.currentPeriodEnd) {
    headline = {
      text: `CANCELLED: paid access ends ${fmt(billing.currentPeriodEnd)}, then drops to Free. No further charges.`,
      className: 'text-amber-700 bg-amber-50 border-amber-200',
    };
  } else if (billing.subscriptionStatus === 'past_due') {
    headline = {
      text: 'PAST DUE: last payment failed; Stripe is retrying. Tier is retained during dunning.',
      className: 'text-red-700 bg-red-50 border-red-200',
    };
  } else if (billing.subscriptionStatus === 'active' && billing.currentPeriodEnd) {
    headline = {
      text: `Active, renews ${fmt(billing.currentPeriodEnd)}.`,
      className: 'text-green-700 bg-green-50 border-green-200',
    };
  } else if (billing.seasonPass) {
    headline = {
      text: `Season Pass: ${billing.seasonPass.tier} access until ${fmt(billing.seasonPass.expiresAt)} (one-time purchase, never renews).`,
      className: 'text-green-700 bg-green-50 border-green-200',
    };
  } else if (!billing.hasStripeCustomer) {
    headline = {
      text: 'No paid subscription. This organization has never checked out.',
      className: 'text-gray-600 bg-gray-50 border-gray-200',
    };
  } else {
    headline = {
      text: `No active subscription (last Stripe status: ${billing.subscriptionStatus ?? 'none'}).`,
      className: 'text-gray-600 bg-gray-50 border-gray-200',
    };
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-gray-900">Billing</h2>
        <span className="text-sm text-gray-500">
          Paid tier: <span className="font-medium text-gray-900">{billing.subscriptionTier}</span>
          {billing.effectiveTier !== billing.subscriptionTier && (
            <> · effective: <span className="font-medium text-gray-900">{billing.effectiveTier}</span> (season pass)</>
          )}
        </span>
      </div>

      <div className={`border rounded-md px-4 py-3 text-sm font-medium mb-4 ${headline.className}`}>
        {headline.text}
      </div>

      {billing.seasonPass && billing.subscriptionStatus === 'active' && (
        <p className="text-sm text-gray-600 mb-4">
          Also holds a Season Pass ({billing.seasonPass.tier}) until {fmt(billing.seasonPass.expiresAt)}.
        </p>
      )}

      {billing.recentEvents.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Billing history (Stripe ledger)</h3>
          <ul className="space-y-1 max-h-48 overflow-y-auto">
            {billing.recentEvents.map((e, i) => (
              <li key={i} className="text-sm text-gray-600 flex justify-between gap-4">
                <span className={e.type === 'invoice.payment_failed' ? 'text-red-600 font-medium' : ''}>
                  {BILLING_EVENT_LABELS[e.type] ?? e.type}
                </span>
                <span className="text-gray-400 whitespace-nowrap">
                  {new Date(e.createdAt).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function InfoCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <p className="text-sm text-gray-500 mb-1">{label}</p>
      <p className="text-lg font-semibold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function TierBadge({ tier }: { tier: string }) {
  const colors: Record<string, string> = {
    FREE: 'bg-gray-100 text-gray-800',
    PRO: 'bg-blue-100 text-blue-800',
    ENTERPRISE: 'bg-purple-100 text-purple-800',
  };

  return (
    <span
      className={`inline-flex px-2.5 py-0.5 text-xs font-medium ${
        colors[tier] || colors.FREE
      }`}
    >
      {tier}
    </span>
  );
}

function StatusBadge({ isActive }: { isActive: boolean }) {
  return (
    <span
      className={`inline-flex px-2.5 py-0.5 text-xs font-medium ${
        isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
      }`}
    >
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
}

function AdminsTable({ admins }: { admins: OrgAdmin[] }) {
  const [resetUserId, setResetUserId] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetMessage, setResetMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleReset = async () => {
    if (!resetUserId || !newPassword) return;
    setResetting(true);
    setResetMessage(null);
    try {
      const result = await platformResetUserPassword(resetUserId, newPassword);
      setResetMessage({ type: 'success', text: result.message });
      setNewPassword('');
      setTimeout(() => {
        setResetUserId(null);
        setResetMessage(null);
      }, 2000);
    } catch (err) {
      setResetMessage({ type: 'error', text: getErrorMessage(err) });
    } finally {
      setResetting(false);
    }
  };

  if (admins.length === 0) {
    return (
      <div className="px-6 py-8 text-center text-gray-500">
        No admins or owners found
      </div>
    );
  }

  return (
    <>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Name
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Email
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Role
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Last Login
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Password Set
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {admins.map((admin) => (
            <tr key={admin.id}>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">
                  {admin.firstName} {admin.lastName}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-500">{admin.email}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <RoleBadge role={admin.role} />
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-500">
                  {admin.lastLoginAt ? formatDateTime(admin.lastLoginAt) : 'Never'}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-500">
                  {admin.passwordSetAt ? formatDateTime(admin.passwordSetAt) : 'Never'}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right">
                <button
                  onClick={() => { setResetUserId(admin.id); setNewPassword(''); setResetMessage(null); }}
                  className="text-sm text-primary-600 hover:text-primary-800 font-medium"
                >
                  Reset Password
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Reset Password Modal */}
      <Modal
        isOpen={!!resetUserId}
        onClose={() => setResetUserId(null)}
        size="sm"
        labelledBy="reset-password-title"
      >
        <div className="p-6">
          <h3 id="reset-password-title" className="text-lg font-semibold text-gray-900 mb-1">Reset Password</h3>
          <p className="text-sm text-gray-500 mb-4">
            {admins.find((a) => a.id === resetUserId)?.email}
          </p>

          {resetMessage && (
            <div className={`mb-3 px-3 py-2 rounded-md text-sm ${
              resetMessage.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}>
              {resetMessage.text}
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack"
              placeholder="Min 8 characters"
              autoFocus
              autoComplete="new-password"
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setResetUserId(null)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleReset}
              disabled={resetting || newPassword.length < 8}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                resetting || newPassword.length < 8
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              {resetting ? 'Resetting...' : 'Set Password'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

function ActivityLogSection({ orgId }: { orgId: string }) {
  const [logs, setLogs] = useState<OrgLogsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const loadLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getPlatformOrganizationLogs(orgId, 24);
      setLogs(data);
      setExpanded(true);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const blob = await downloadOrgLogs(orgId);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `activity-logs-${new Date().toISOString().slice(0, 10)}.jsonl`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-8">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Activity Log</h2>
        <div className="flex items-center gap-3">
          {logs && (
            <span className="text-xs text-gray-500">
              {logs.totalLogSizeFormatted} total
            </span>
          )}
          <button
            onClick={handleDownload}
            className="text-sm text-gray-600 hover:text-gray-900 font-medium flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Download All
          </button>
          <button
            onClick={loadLogs}
            disabled={loading}
            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Loading...' : expanded ? 'Refresh' : 'View Last 24h'}
          </button>
        </div>
      </div>

      {error && (
        <div className="px-6 py-3 bg-red-50 border-b border-red-200 text-sm text-red-700">
          {error}
        </div>
      )}

      {expanded && logs && (
        <div className="max-h-96 overflow-y-auto">
          {logs.entries.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              No activity in the last 24 hours
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.entries.map((entry, i) => (
                  <tr key={`${entry.timestamp}-${entry.userId}-${i}`} className="hover:bg-gray-50">
                    <td className="px-6 py-2 whitespace-nowrap text-xs text-gray-500">
                      {new Date(entry.timestamp).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        second: '2-digit',
                      })}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-900">
                      {entry.userEmail || entry.userId}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-700">
                      {entry.description}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-xs text-gray-500">
                      {entry.resourceType && (
                        <span className="inline-flex px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                          {entry.resourceType}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function RoleBadge({ role }: { role: 'OWNER' | 'ADMIN' }) {
  const colors = {
    OWNER: 'bg-amber-100 text-amber-800',
    ADMIN: 'bg-blue-100 text-blue-800',
  };

  return (
    <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium ${colors[role]}`}>
      {role}
    </span>
  );
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function PageSkeleton() {
  return (
    <div className="animate-pulse">
      {/* Header */}
      <div className="mb-6">
        <div className="h-4 bg-gray-200 rounded w-32 mb-4"></div>
        <div className="flex items-center gap-4 mb-2">
          <div className="h-8 bg-gray-200 rounded w-64"></div>
          <div className="h-5 bg-gray-200 rounded w-16"></div>
          <div className="h-5 bg-gray-200 rounded w-16"></div>
        </div>
        <div className="h-4 bg-gray-200 rounded w-24"></div>
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
            <div className="h-6 bg-gray-200 rounded w-16"></div>
          </div>
        ))}
      </div>

      {/* Tier section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <div className="h-6 bg-gray-200 rounded w-40 mb-4"></div>
        <div className="flex items-end gap-4">
          <div className="flex-1 max-w-xs">
            <div className="h-4 bg-gray-200 rounded w-12 mb-2"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
          </div>
          <div className="h-10 bg-gray-200 rounded w-16"></div>
        </div>
      </div>

      {/* Admins table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="h-6 bg-gray-200 rounded w-48"></div>
        </div>
        <div className="p-6">
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-10 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
